/**
 * BOM Service
 *
 * High-level service that integrates BOM calculator with cache
 */

import { BomCalculator } from './calculator';
import { apiCache, settingsCache } from '../cache';
import { fetchBestItemPrice, fetchItemPricesBatch } from '../api/echoes';
import type { ItemBlueprint, BomResult, BomOptions } from '@/types';

/**
 * BOM Service Class
 */
export class BomService {
  private calculator: BomCalculator | null = null;

  /**
   * Initialize the BOM calculator with cached data
   */
  async initialize(): Promise<void> {
    try {
      // Get cached data
      const [blueprints, skills] = await Promise.all([
        apiCache.getItemBlueprints(),
        apiCache.getIndustrySkills(),
      ]);

      // Create calculator instance
      this.calculator = new BomCalculator(blueprints, skills);
    } catch (error) {
      console.error('BomService: Initialization failed:', error);
      this.calculator = null;
      throw error;
    }
  }

  /**
   * Ensure calculator is initialized (with auto-reinitialize)
   */
  private async ensureInitialized(): Promise<BomCalculator> {
    if (!this.calculator) {
      console.warn('BomService calculator is null - attempting to reinitialize...');
      await this.initialize();

      if (!this.calculator) {
        throw new Error('BomService initialization failed.');
      }
    }
    return this.calculator;
  }

  /**
   * Calculate BOM for a blueprint by item name
   */
  async calculateByName(blueprintName: string, options: BomOptions = {}): Promise<BomResult> {
    await this.ensureInitialized();

    // Get all blueprints
    const blueprints = await apiCache.getItemBlueprints();

    // Find blueprint by name
    const blueprint = blueprints.find(
      (bp) => bp.name.toLowerCase() === blueprintName.toLowerCase()
    );

    if (!blueprint) {
      throw new Error(`Blueprint not found: ${blueprintName}`);
    }

    return this.calculate(blueprint, options);
  }

  /**
   * Calculate BOM for a blueprint by item ID
   */
  async calculateByItemId(itemId: number, options: BomOptions = {}): Promise<BomResult> {
    await this.ensureInitialized();

    // Get all blueprints
    const blueprints = await apiCache.getItemBlueprints();

    // Find blueprint by item_id
    const blueprint = blueprints.find((bp) => bp.item_id === itemId);

    if (!blueprint) {
      throw new Error(`Blueprint not found for item ID: ${itemId}`);
    }

    return this.calculate(blueprint, options);
  }

  /**
   * Calculate BOM for a blueprint
   */
  async calculate(blueprint: ItemBlueprint, options: BomOptions = {}): Promise<BomResult> {
    const calculator = await this.ensureInitialized();

    // Get user skills from cache if not provided
    let userSkills = options.userSkills;
    if (!userSkills) {
      userSkills = await settingsCache.getUserSkills();
    }

    // Merge options with user skills
    const fullOptions: BomOptions = {
      ...options,
      userSkills,
    };

    // Calculate BOM
    return calculator.calculate(blueprint, fullOptions);
  }

  /**
   * Calculate BOM with current user skills and structure bonuses
   */
  async calculateWithUserSkills(
    blueprint: ItemBlueprint,
    targetQuantity: number = 1
  ): Promise<BomResult> {
    const [userSkills, structureBonuses] = await Promise.all([
      settingsCache.getUserSkills(),
      settingsCache.getStructureBonuses(),
    ]);

    // Calculate total structure ME bonus (sum all structures)
    // The calculator will automatically use all applicable structures
    const totalStructureMEBonus = structureBonuses.structures.reduce(
      (sum, structure) => sum + structure.meBonus,
      0
    );

    const result = await this.calculate(blueprint, {
      targetQuantity,
      userSkills,
      structureBonuses: {
        manufacturingME: totalStructureMEBonus,
      },
      applyEfficiency: true,
      includeIntermediates: true,
    });

    // Enrich with market pricing data
    return this.enrichWithPricing(result, blueprint);
  }

  /**
   * Enrich BOM result with market pricing data
   */
  private async enrichWithPricing(result: BomResult, blueprint: ItemBlueprint): Promise<BomResult> {
    try {
      // Fetch target item price
      const targetItemPrice = await fetchBestItemPrice(blueprint.item_id);

      // Collect all raw material item IDs
      const rawMaterialIds = result.rawMaterials.map((m) => parseInt(m.itemId, 10));

      // Fetch prices for all raw materials in batch
      const priceMap = await fetchItemPricesBatch(rawMaterialIds);

      // Update raw materials with pricing
      const enrichedRawMaterials = result.rawMaterials.map((material) => {
        const itemId = parseInt(material.itemId, 10);
        const unitPrice = priceMap.get(itemId);
        const totalPrice =
          unitPrice !== undefined ? unitPrice * material.adjustedQuantity : undefined;

        return {
          ...material,
          unitPrice,
          totalPrice,
        };
      });

      // Calculate total market cost
      const totalMarketCost = enrichedRawMaterials.reduce((sum, material) => {
        return sum + (material.totalPrice ?? 0);
      }, 0);

      // Calculate manufacturing costs
      const targetItemManufactureCost = blueprint.manufacture_cost * result.targetQuantity;
      const totalIntermediateManufactureCost = result.intermediateItems.reduce((sum, item) => {
        return sum + item.blueprint.manufacture_cost * item.runs;
      }, 0);

      return {
        ...result,
        rawMaterials: enrichedRawMaterials,
        targetItemPrice: targetItemPrice ?? undefined,
        totalMarketCost: totalMarketCost > 0 ? totalMarketCost : undefined,
        targetItemManufactureCost,
        totalIntermediateManufactureCost:
          totalIntermediateManufactureCost > 0 ? totalIntermediateManufactureCost : undefined,
      };
    } catch (error) {
      console.error('Failed to fetch pricing data:', error);
      // Return original result if pricing fails
      return result;
    }
  }

  /**
   * Calculate BOM without any skill bonuses (baseline)
   */
  async calculateBaseline(
    blueprint: ItemBlueprint,
    targetQuantity: number = 1
  ): Promise<BomResult> {
    return this.calculate(blueprint, {
      targetQuantity,
      userSkills: {},
      applyEfficiency: false,
      includeIntermediates: true,
    });
  }

  /**
   * Compare BOM with and without skills
   */
  async compareWithSkills(
    blueprint: ItemBlueprint,
    targetQuantity: number = 1
  ): Promise<{
    withSkills: BomResult;
    withoutSkills: BomResult;
    savings: {
      [itemId: string]: {
        itemName: string;
        saved: number;
        percentSaved: number;
      };
    };
  }> {
    const [withSkills, withoutSkills] = await Promise.all([
      this.calculateWithUserSkills(blueprint, targetQuantity),
      this.calculateBaseline(blueprint, targetQuantity),
    ]);

    // Calculate savings per material
    const savings: {
      [itemId: string]: {
        itemName: string;
        saved: number;
        percentSaved: number;
      };
    } = {};

    withoutSkills.rawMaterials.forEach((noSkillMat) => {
      const withSkillMat = withSkills.rawMaterials.find((m) => m.itemId === noSkillMat.itemId);

      if (withSkillMat) {
        const saved = noSkillMat.adjustedQuantity - withSkillMat.adjustedQuantity;
        const percentSaved = (saved / noSkillMat.adjustedQuantity) * 100;

        savings[noSkillMat.itemId] = {
          itemName: noSkillMat.itemName,
          saved,
          percentSaved,
        };
      }
    });

    return {
      withSkills,
      withoutSkills,
      savings,
    };
  }

  /**
   * Get all available blueprints (for selection UI)
   */
  async getAvailableBlueprints(): Promise<ItemBlueprint[]> {
    return apiCache.getItemBlueprints();
  }

  /**
   * Search blueprints by name
   */
  async searchBlueprints(query: string): Promise<ItemBlueprint[]> {
    const blueprints = await this.getAvailableBlueprints();
    const lowerQuery = query.toLowerCase();

    return blueprints.filter((bp) => bp.name.toLowerCase().includes(lowerQuery));
  }
}

/**
 * Singleton instance
 */
export const bomService = new BomService();
